import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Phone, ArrowRight, CheckCircle, Building2, Droplets, Home as HomeIcon, Car, TreePine, Fence } from 'lucide-react';

const HERO_IMAGE = "https://images.pexels.com/photos/17174768/pexels-photo-17174768.jpeg?auto=compress&cs=tinysrgb&w=1920";
const HERO_LOGO = "https://customer-assets.emergentagent.com/job_rade-gradnja/artifacts/zu4djkks_Logo%20RadeGradnja%20i%20Renovacije.png";

export const HomePage = () => {
  const { t } = useLanguage();

  const stats = [
    { value: "20+", label: t('about.experience') },
    { value: "150+", label: t('about.projects') },
    { value: "100%", label: t('about.clients') },
    { value: "Srbija", label: t('about.area') }
  ];

  const services = [
    { icon: HomeIcon, title: t('services.turnkey.title'), description: t('services.turnkey.description'), featured: true },
    { icon: Building2, title: t('services.construction.title'), description: t('services.construction.description') },
    { icon: Droplets, title: t('services.pools.title'), description: t('services.pools.description') },
    { icon: TreePine, title: t('services.gazebos.title'), description: t('services.gazebos.description') },
    { icon: Car, title: t('services.garages.title'), description: t('services.garages.description') },
    { icon: Fence, title: t('services.terraces.title'), description: t('services.terraces.description') }
  ];

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_IMAGE} 
            alt="Construction" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0B1E3B]/80"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 pt-40">
          <div className="flex flex-col items-center text-center">
            {/* Hero Logo */}
            <div className="mb-6 animate-fade-in-up">
              <img 
                src={HERO_LOGO} 
                alt="Rade Gradnja STD Logo" 
                className="w-56 md:w-64 lg:w-72 h-auto drop-shadow-2xl"
              />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-none mb-4 font-['Barlow_Condensed'] animate-fade-in-up animation-delay-200">
              {t('hero.title')}
            </h1>
            <p className="text-2xl md:text-3xl text-[#D4AF37] font-medium mb-6 font-['Barlow_Condensed'] animate-fade-in-up animation-delay-300">
              {t('hero.subtitle')}
            </p>
            <p className="text-lg text-gray-300 mb-8 max-w-xl leading-relaxed animate-fade-in-up animation-delay-400">
              {t('hero.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-400">
              <Link to="/kontakt">
                <Button 
                  size="lg" 
                  className="bg-[#D4AF37] text-[#0B1E3B] hover:bg-[#D4AF37]/90 font-semibold px-8 py-6 text-base"
                  data-testid="hero-cta-contact"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  {t('hero.cta_contact')}
                </Button>
              </Link>
              <Link to="/usluge">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-white text-white hover:bg-white hover:text-[#0B1E3B] font-semibold px-8 py-6 text-base"
                  data-testid="hero-cta-services"
                >
                  {t('hero.cta_services')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/70 rounded-full animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-16" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-[#0B1E3B] font-['Barlow_Condensed'] mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 text-sm uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="bg-[#F8F9FA] py-20" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
                {t('about.tagline')}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#0B1E3B] font-['Barlow_Condensed'] mb-6">
                {t('about.title')}
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                {t('about.description')}
              </p>
              <div className="space-y-4">
                {[
                  'Profesionalan tim sa dugogodišnjim iskustvom',
                  'Kvalitetni materijali i moderna oprema',
                  'Transparentne cene bez skrivenih troškova',
                  'Poštovanje rokova i dogovorenog budžeta'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37]" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_rade-gradnja/artifacts/vcp3n0hw_Slika%20radnika.png"
                alt="Radnik na gradilištu"
                className="rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#D4AF37] text-[#0B1E3B] p-6 rounded-lg shadow-lg">
                <div className="text-3xl font-bold font-['Barlow_Condensed']">20+</div>
                <div className="text-sm uppercase tracking-wider">{t('about.experience')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="bg-white py-20" data-testid="services-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4 block">
              {t('services.tagline')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1E3B] font-['Barlow_Condensed'] mb-4">
              {t('services.title')}
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
                className={`group p-6 rounded-lg transition-all duration-300 hover:shadow-lg ${
                  service.featured 
                    ? 'bg-[#0B1E3B] text-white md:col-span-2 lg:col-span-1' 
                    : 'bg-[#F8F9FA] hover:bg-white'
                }`}
                data-testid={`service-card-${index}`}
              >
                <service.icon className={`h-10 w-10 mb-4 ${service.featured ? 'text-[#D4AF37]' : 'text-[#0B1E3B]'}`} />
                <h3 className={`text-xl font-bold mb-3 font-['Barlow_Condensed'] ${service.featured ? 'text-white' : 'text-[#0B1E3B]'}`}>
                  {service.title}
                </h3>
                <p className={`text-sm leading-relaxed ${service.featured ? 'text-gray-300' : 'text-gray-600'}`}>
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/usluge">
              <Button 
                variant="outline" 
                size="lg"
                className="border-[#0B1E3B] text-[#0B1E3B] hover:bg-[#0B1E3B] hover:text-white font-semibold"
                data-testid="view-all-services-btn"
              >
                {t('hero.cta_services')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0B1E3B] py-16" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white font-['Barlow_Condensed'] mb-4">
            {t('contact.area.title')}
          </h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            {t('contact.area.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+381631627662">
              <Button 
                size="lg"
                className="bg-[#D4AF37] text-[#0B1E3B] hover:bg-[#D4AF37]/90 font-semibold px-8"
                data-testid="cta-phone-btn"
              >
                <Phone className="mr-2 h-5 w-5" />
                +381 63 162 7662
              </Button>
            </a>
            <a 
              href="viber://chat?number=+381631627662"
              className="viber-btn text-white font-semibold px-8 py-3 rounded-md flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
              data-testid="cta-viber-btn"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.031 1.003c-4.496-.062-8.795 1.727-10.615 5.83C.1 9.67-.246 12.623.218 15.86c.328 2.294 1.027 4.24 2.378 5.93.636.793 1.26 1.59 1.866 2.405.32.431.625.87.94 1.305.168.233.343.463.503.703.108.163.202.336.309.5.045.068.196.289.196.289l-.008.008c-.233.31-1.106 1.28-1.106 1.28l.012-.004s.367.36.875.36c.196 0 .423-.055.65-.193.544-.332 2.413-1.615 3.246-2.235.15-.111.333-.172.521-.172h.2c1.623.102 3.251.097 4.87-.075 2.06-.219 3.984-.79 5.664-1.99 2.527-1.805 3.886-4.324 4.267-7.365.278-2.224.023-4.363-.836-6.397C22.5 5.582 19.36 2.608 15.022 1.392c-.99-.277-1.993-.39-2.991-.389zm-.044 1.643c.876.002 1.77.1 2.663.34 3.633.975 6.305 3.514 7.46 7.132.692 2.173.681 4.369.004 6.545-.74 2.38-2.24 4.152-4.474 5.228-1.663.801-3.449 1.072-5.28 1.072-.7 0-1.409-.033-2.116-.092l-.204-.017a1.97 1.97 0 0 0-1.256.407c-.723.539-2.323 1.638-2.836 1.953.19-.253.85-1.115.991-1.295l.004-.006c.29-.386.245-.921-.11-1.252a79.527 79.527 0 0 1-1.104-1.096l-.13-.133c-.95-1.043-1.68-2.2-2.075-3.567-.565-1.953-.508-3.91.146-5.822.855-2.492 2.543-4.256 4.902-5.362 1.388-.65 2.86-.993 4.402-1.031.346-.01.694-.014 1.04-.013z"/>
              </svg>
              Viber
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};
